import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaUser } from 'react-icons/fa';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const questionSections = [
  {
    title: 'General Questions',
    fields: [
      {
        key: 'general_experience',
        label: '1. What experience do you have in football scouting or coaching?',
      },
      {
        key: 'general_identify_talent',
        label: '2. How do you identify talented young players?',
      },
      {
        key: 'general_age_groups',
        label: '3. What age groups have you worked with, and what were your responsibilities?',
      },
    ],
  },
  {
    title: 'Scouting Approach',
    fields: [
      {
        key: 'scouting_trends',
        label: '1. How do you stay updated on local and international football trends?',
      },
      {
        key: 'scouting_evaluation',
        label: "2. Describe your process for evaluating a player's technical, tactical, physical, and psychological attributes.",
      },
      {
        key: 'scouting_potential',
        label: "3. How do you assess a player's potential for development?",
      },
    ],
  },
  {
    title: 'Network & Relationships',
    fields: [
      {
        key: 'network_build',
        label: '1. How do you build relationships with coaches, agents, and other scouts?',
      },
      {
        key: 'network_existing',
        label: '2. Do you have an existing network of contacts in Ghana or globally?',
      },
      {
        key: 'network_collaborate',
        label: '3. How do you collaborate with academy coaches and management?',
      },
    ],
  },
  {
    title: 'Evaluation & Decision-Making',
    fields: [
      {
        key: 'eval_criteria',
        label: '1. What criteria do you use to recommend a player for the academy?',
      },
      {
        key: 'eval_example',
        label: '2. Can you give an example of a player you identified and how you handled the process?',
      },
      {
        key: 'eval_instinct_data',
        label: '3. How do you balance instinct with data-driven decision-making?',
      },
    ],
  },
  {
    title: 'Logistics & Administration',
    fields: [
      {
        key: 'logistics_schedule',
        label: '1. How do you manage your scouting schedule and travel?',
      },
      {
        key: 'logistics_tools',
        label: '2. What tools or software do you use for player tracking and reporting?',
      },
      {
        key: 'logistics_compliance',
        label: '3. How do you ensure compliance with regulations (e.g., FIFA, GFA)?',
      },
    ],
  },
  {
    title: 'KSA Specifics',
    fields: [
      {
        key: 'ksa_knowledge',
        label: '1. What do you know about our academy and its values?',
      },
      {
        key: 'ksa_contribute',
        label: "2. How would you contribute to our academy's growth and success?",
      },
      {
        key: 'ksa_improve',
        label: '3. Do you have any ideas for improving our scouting network?',
      },
    ],
  },
  {
    title: 'Security',
    fields: [
      {
        key: 'security_hail_from_location',
        label: '1. Do you hail from your present location?',
      },
      {
        key: 'security_present_living',
        label: '2. What are you doing presently for a living?',
      },
      {
        key: 'security_residence_status',
        label: '3. Residence Status',
      },
      {
        key: 'security_residence_other',
        label: '4. If other, please specify your residence status',
      },
      {
        key: 'security_gps_address',
        label: '5. GPS Address',
      },
      {
        key: 'security_locality',
        label: '6. Locality Name',
      },
      {
        key: 'security_referee_name',
        label: '7. Referee Name',
      },
      {
        key: 'security_referee_relationship',
        label: '8. Referee Relationship',
      },
      {
        key: 'security_referee_phone',
        label: '9. Referee Phone Number',
      },
    ],
  },
];

const getAnswer = (item, key) => {
  if (!item || !key) return '';

  return (
    item[key] ||
    item.answers?.[key] ||
    item.questionnaire?.[key] ||
    item.responses?.[key] ||
    ''
  );
};

const formatAnswerValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : 'No answer provided';
  }

  if (value === null || value === undefined) {
    return 'No answer provided';
  }

  const normalized = `${value}`.trim();
  return normalized ? normalized : 'No answer provided';
};

const normalizeText = (value) => `${value || ''}`.trim().toLowerCase();

const isQuestionnaireRecord = (item) => {
  if (!item) return false;
  return questionSections.some((section) =>
    section.fields.some((field) => normalizeText(getAnswer(item, field.key)) !== '')
  );
};

const getScoutFullName = (item) => {
  const combined = `${item?.first_name || ''} ${item?.last_name || ''}`.trim();
  if (combined) return combined;
  return item?.name || item?.full_name || 'Scout';
};

export default function ScoutQuestionnairesView() {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedScout = location.state?.scout || null;
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [applicationDecisions, setApplicationDecisions] = useState({});
  const [decisionNotes, setDecisionNotes] = useState({});
  const [submittingDecisions, setSubmittingDecisions] = useState({});
  const [submittedDecisions, setSubmittedDecisions] = useState({});

  useEffect(() => {
    const fetchQuestionnaires = async () => {
      if (!selectedScout?.id) {
        setQuestionnaires([]);
        return;
      }

      try {
        setLoading(true);
        setFetchError('');

        const response = await axios.get(' https://ksa-manager-back.onrender.com/scout_questionnaires', {
          params: { scout_id: selectedScout.id },
          headers: { Accept: 'application/json' },
        });

        const fetchedQuestionnaires = Array.isArray(response.data?.questionnaires)
          ? response.data.questionnaires
          : [];

        setQuestionnaires(fetchedQuestionnaires);
      } catch (error) {
        console.error('Error fetching scout questionnaires:', error);
        setFetchError(
          error.response?.data?.message || 'Failed to fetch scout questionnaires.'
        );
        setQuestionnaires([]);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionnaires();
  }, [selectedScout]);

  const visibleQuestionnaires = useMemo(() => {
    if (!selectedScout) {
      return [];
    }

    return questionnaires.filter(isQuestionnaireRecord).map((item) => ({
      ...item,
      first_name: item.first_name || selectedScout.first_name,
      last_name: item.last_name || selectedScout.last_name,
      email: item.email || selectedScout.email,
      scout_id: item.scout_id || selectedScout.id,
    }));
  }, [questionnaires, selectedScout]);

  const getDecision = (item, index) => {
    const decisionKey = `${item.id || item.scout_id || index}`;
    return applicationDecisions[decisionKey] || item.application_status || 'pending';
  };

  const handleDecision = (item, index, decision) => {
    const decisionKey = `${item.id || item.scout_id || index}`;
    setApplicationDecisions((prev) => ({
      ...prev,
      [decisionKey]: decision,
    }));
  };

  const handleToggleDecision = (item, index) => {
    const currentDecision = getDecision(item, index);
    const nextDecision = currentDecision === 'approved' ? 'rejected' : 'approved';
    handleDecision(item, index, nextDecision);
  };

  const handleNoteChange = (item, index, value) => {
    const decisionKey = `${item.id || item.scout_id || index}`;
    setDecisionNotes((prev) => ({
      ...prev,
      [decisionKey]: value,
    }));
  };

  const handleSubmitDecision = async (item, index) => {
    const decisionKey = `${item.id || item.scout_id || index}`;
    const decision = applicationDecisions[decisionKey];
    const notes = decisionNotes[decisionKey] ?? item.decision_notes ?? '';

    if (!decision || decision === 'pending') {
      toast.warning('Please select Approve or Reject before submitting.');
      return;
    }

    try {
      setSubmittingDecisions((prev) => ({
        ...prev,
        [decisionKey]: true,
      }));

      await axios.patch(
        ` https://ksa-manager-back.onrender.com/scout_questionnaires/${item.id}/decision`,
        {
          application_status: decision,
          decision_notes: notes,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        }
      );

      setQuestionnaires((prev) =>
        prev.map((questionnaire) =>
          questionnaire.id === item.id
            ? {
                ...questionnaire,
                application_status: decision,
                decision_notes: notes,
              }
            : questionnaire
        )
      );

      setSubmittedDecisions((prev) => ({
        ...prev,
        [decisionKey]: decision,
      }));

      toast.success(
        `Decision submitted: ${decision === 'approved' ? 'Approved' : 'Rejected'}.`
      );
    } catch (error) {
      console.error('Error updating questionnaire decision:', error);
      toast.error(
        error.response?.data?.message || 'Failed to update application status.'
      );
    } finally {
      setSubmittingDecisions((prev) => ({
        ...prev,
        [decisionKey]: false,
      }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-12">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      <div className="mx-auto max-w-7xl">
        <button
          onClick={() => navigate('/admin/warehouse-view')}
          className="mb-6 inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-gray-200 transition-colors hover:bg-gray-100"
        >
          <FaArrowLeft className="text-xs" />
          Back to Registered Scouts
        </button>

        <div className="mb-8 rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
          <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
            Scout Questionnaire Answers
          </h1>
          <p className="mt-2 text-sm text-gray-700 md:text-base">
            {selectedScout
              ? `Viewing questionnaire answers for ${getScoutFullName(selectedScout)}.`
              : 'Open this page from a scout row to view one registration detail.'}
          </p>
        </div>

        {loading && (
          <div className="rounded-xl bg-white p-10 text-center shadow-sm ring-1 ring-gray-100">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
            <p className="mt-4 text-sm font-medium text-gray-700">
              Loading questionnaire answers...
            </p>
          </div>
        )}

        {!loading && fetchError && (
          <div className="rounded-xl bg-red-50 p-6 text-center shadow-sm ring-1 ring-red-100">
            <p className="text-sm font-medium text-red-700">{fetchError}</p>
          </div>
        )}

        {!loading && !fetchError && visibleQuestionnaires.length === 0 && (
          <div className="rounded-xl bg-white p-10 text-center shadow-sm ring-1 ring-gray-100">
            <p className="text-sm font-medium text-gray-700">
              No questionnaire answers were found for this scout.
            </p>
          </div>
        )}

        {!loading && !fetchError && visibleQuestionnaires.length > 0 && (
          <div className="space-y-6">
            {visibleQuestionnaires.map((item, index) => {
              const submissionDate =
                item.created_at || item.createdAt || item.submitted_at || item.timestamp;
              const currentDecision = getDecision(item, index);
              const decisionKey = `${item.id || item.scout_id || index}`;
              const isSubmitted = Boolean(submittedDecisions[decisionKey]);
              const isSubmittingDecision = Boolean(submittingDecisions[decisionKey]);
              const currentNotes = decisionNotes[decisionKey] ?? item.decision_notes ?? '';

              return (
                <div
                  key={`${item.id || item.scout_id || index}`}
                  className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-100"
                >
                  <div className="border-b border-gray-100 bg-gray-50 px-6 py-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                          <FaUser className="text-blue-600" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900">
                            {getScoutFullName(item)}
                          </h2>
                          <p className="text-xs text-gray-500">
                            {item.email || item.scout_email || 'No email available'}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-col items-start gap-2 sm:items-end">
                        {submissionDate && (
                          <p className="text-xs font-medium text-gray-700">
                            Submitted on {new Date(submissionDate).toLocaleString()}
                          </p>
                        )}
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                            currentDecision === 'approved'
                              ? 'bg-emerald-100 text-emerald-800'
                              : currentDecision === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {currentDecision === 'approved'
                            ? 'Approved'
                            : currentDecision === 'rejected'
                            ? 'Rejected'
                            : 'Pending Review'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6 p-6">
                    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
                      <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Submission Overview
                        </p>
                        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                          <div className="rounded-lg border border-slate-200 bg-white p-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Questionnaire ID
                            </p>
                            <p className="mt-2 text-lg font-bold text-slate-900">
                              #{item.id || 'N/A'}
                            </p>
                          </div>
                          <div className="rounded-lg border border-slate-200 bg-white p-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Scout ID
                            </p>
                            <p className="mt-2 text-lg font-bold text-slate-900">
                              {item.scout_id || 'N/A'}
                            </p>
                          </div>
                          <div className="rounded-lg border border-slate-200 bg-white p-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Current Status
                            </p>
                            <p className="mt-2 text-lg font-bold capitalize text-slate-900">
                              {item.application_status || 'pending'}
                            </p>
                          </div>
                          <div className="rounded-lg border border-slate-200 bg-white p-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Scout Offer Decision
                            </p>
                            {(() => {
                              const offerStatus = normalizeText(item.offer_status) || 'pending';
                              const offerStyles =
                                offerStatus === 'accepted'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : offerStatus === 'denied'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-amber-100 text-amber-800';
                              const offerLabel =
                                offerStatus === 'accepted'
                                  ? 'Accepted'
                                  : offerStatus === 'denied'
                                  ? 'Denied'
                                  : 'Pending';
                              return (
                                <span
                                  className={`mt-2 inline-flex rounded-full px-3 py-1 text-sm font-bold ${offerStyles}`}
                                >
                                  {offerLabel}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                        <div className="border-b border-slate-100 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Passport Photo
                          </p>
                        </div>
                        {item.security_passport_photo ? (
                          <div className="p-4">
                            <img
                              src={item.security_passport_photo}
                              alt={`${getScoutFullName(item)} passport`}
                              className="h-72 w-full rounded-lg object-cover ring-1 ring-slate-200"
                            />
                          </div>
                        ) : (
                          <div className="flex h-72 items-center justify-center px-6 text-center text-sm font-medium text-slate-500">
                            No passport photo uploaded.
                          </div>
                        )}
                      </div>
                    </div>

                    <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <h3 className="text-base font-bold text-slate-900">Application Review</h3>
                          <p className="mt-1 text-sm text-slate-600">
                            Set the application status and add optional notes before submitting.
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-red-700">Reject</span>
                          <button
                            type="button"
                            onClick={() => handleToggleDecision(item, index)}
                            disabled={isSubmittingDecision}
                            className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors ${
                              currentDecision === 'approved'
                                ? 'bg-green-600'
                                : currentDecision === 'rejected'
                                ? 'bg-red-600'
                                : 'bg-gray-400'
                            } ${isSubmittingDecision ? 'cursor-not-allowed opacity-70' : ''}`}
                          >
                            <span
                              className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                                currentDecision === 'approved' ? 'translate-x-9' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <span className="text-sm font-semibold text-green-700">Approve</span>
                        </div>
                      </div>

                      <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
                        <div>
                          <label className="mb-2 block text-sm font-semibold text-slate-800">
                            Decision Notes (Optional)
                          </label>
                          <textarea
                            value={currentNotes}
                            onChange={(e) => handleNoteChange(item, index, e.target.value)}
                            placeholder="Add review notes for this scout application..."
                            rows={4}
                            className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                          />
                        </div>

                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                            Ready To Submit
                          </p>
                          <p className="mt-3 text-sm font-medium text-slate-700">
                            Selected status:
                          </p>
                          <p className="mt-1 text-lg font-bold capitalize text-slate-900">
                            {currentDecision === 'pending' ? 'Not selected' : currentDecision}
                          </p>
                          <button
                            onClick={() => handleSubmitDecision(item, index)}
                            disabled={isSubmittingDecision}
                            className={`mt-5 w-full rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                              isSubmittingDecision
                                ? 'cursor-not-allowed bg-gray-300 text-gray-600'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            {isSubmittingDecision ? 'Submitting...' : 'Submit Decision'}
                          </button>
                          {isSubmitted && !isSubmittingDecision && (
                            <p className="mt-3 text-xs font-medium text-emerald-700">
                              Decision saved successfully.
                            </p>
                          )}
                        </div>
                      </div>
                    </section>

                    {questionSections.map((section) => (
                      <section key={section.title} className="rounded-xl border border-slate-200 bg-slate-50/70 p-5">
                        <h3 className="mb-4 text-base font-bold text-slate-900">
                          {section.title}
                        </h3>
                        <div className="space-y-4">
                          {section.fields.map((field) => (
                            <div
                              key={field.key}
                              className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[minmax(0,0.95fr)_minmax(0,1.4fr)]"
                            >
                              <div className="rounded-lg bg-slate-50 p-4">
                                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
                                  Question
                                </p>
                                <p className="mt-2 text-sm font-semibold leading-6 text-slate-900">
                                  {field.label}
                                </p>
                              </div>
                              <div className="rounded-lg bg-blue-50/60 p-4 ring-1 ring-blue-100">
                                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-blue-700">
                                  Answer
                                </p>
                                <p className="mt-2 whitespace-pre-wrap text-[15px] leading-7 text-slate-800">
                                  {formatAnswerValue(getAnswer(item, field.key))}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </section>
                    ))}

                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}